import React from 'react';
import type { ViewImages, ViewKey } from './carWizardTypes';
import { VIEW_KEYS } from './carWizardTypes';
import ViewSelector from './ViewSelector';
import type { Layer } from './LayeredPreview';

interface Props {
  showSummary: boolean;
  showContact: boolean;
  currentStepIndex: number;
  totalSteps: number;
  isLastUiStep: boolean;
  isNextDisabled?: boolean;
  viewLoading: boolean;
  selectedView: ViewKey;
  setSelectedView: (view: ViewKey) => void;
  setViewLoading: (loading: boolean) => void;
  currentImageSource: { images: ViewImages } | null;
  getThumbnailForView: (viewKey: ViewKey) => Layer[];

  copySuccess: boolean;
  handleSaveSummary: () => void;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  onContact: () => void;

  totalPrice: number;
  embedded?: boolean;
}

const ConfiguratorFooter: React.FC<Props> = ({
  showSummary,
  showContact,
  currentStepIndex,
  totalSteps,
  isLastUiStep,
  isNextDisabled = false,
  viewLoading,
  selectedView,
  setSelectedView,
  setViewLoading,
  currentImageSource,
  getThumbnailForView,
  copySuccess,
  handleSaveSummary,
  goToPreviousStep,
  goToNextStep,
  onContact,
  totalPrice,
  embedded = false,
}) => {
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  return (
    <div
      className="bg-white mt-auto shadow-[0_-6px_18px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
    >
      <div className="w-full h-px bg-gray-200">
        <div className="h-full bg-black transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="p-4 md:p-5 md:px-6">
        <div className={embedded ? 'hidden' : 'hidden lg:block'}>
          <ViewSelector
            showSummary={showSummary}
            viewLoading={viewLoading}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            setViewLoading={setViewLoading}
            currentImageSource={currentImageSource}
            getThumbnailForView={getThumbnailForView}
            viewKeys={VIEW_KEYS}
          />
        </div>

        {!showSummary && !showContact && isNextDisabled && (
          <div className="mb-3 text-sm text-gray-600">
            Please select the required options to continue.
          </div>
        )}

        <div className="space-y-3 mb-4">
          {showSummary && (
            <button
              onClick={handleSaveSummary}
              className="w-full py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              {copySuccess ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Link Copied!
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
                  </svg>
                  Save & Share Configuration
                </>
              )}
            </button>
          )}

          {copySuccess && showSummary && (
            <div className="text-center text-sm text-green-600 font-medium animate-fadeIn">✓ Shareable link copied to clipboard!</div>
          )}

          <div className="flex gap-3">
            {(currentStepIndex > 0 || showSummary) && (
              <button
                onClick={goToPreviousStep}
                className="flex-1 py-3 bg-white text-black border border-black rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <img
                  src="https://cdn.prod.website-files.com/68472be536d16d9004a489e5/69743ecae4937de4a1b8cf4f_Frame.png"
                  alt="Back"
                  className="w-4 h-4"
                  style={{ transform: 'rotate(180deg)', filter: 'invert(1)' }}
                />
                Back
              </button>
            )}

            {!showSummary && !showContact && (
              <button
                onClick={() => {
                  if (isNextDisabled) return;
                  goToNextStep();
                }}
                disabled={isNextDisabled}
                className={`flex-1 py-3 bg-black text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  isNextDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800 cursor-pointer'
                }`}
              >
                {isLastUiStep ? 'View Summary' : 'Next'}
                <img
                  src="https://cdn.prod.website-files.com/68472be536d16d9004a489e5/69743ecae4937de4a1b8cf4f_Frame.png"
                  alt="Next"
                  className="w-4 h-4"
                />
              </button>
            )}

            {showSummary && !showContact && (
              <button
                onClick={onContact}
                className="flex-1 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                Contact
                <img
                  src="https://cdn.prod.website-files.com/68472be536d16d9004a489e5/69c2f77e872fffc7542078c5_IcOutlineCheck.png"
                  alt="Check"
                  className="w-4 h-4"
                  style={{ filter: 'invert(1)' }}
                />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center py-3 border-t border-gray-200 px-4 md:px-6">
        <span className="text-xl font-semibold text-black">Total</span>
        <span className="text-3xl font-bold text-black">
          {totalPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })} USD
        </span>
      </div>
    </div>
  );
};

export default ConfiguratorFooter;
